/**
 * Picks the "keep reading" neighbours for an article.
 *
 * The module used to take `all.slice(0, 2)`, which handed every post the same
 * two newest articles and made it useless for internal linking. Selection is
 * now scored: the article's one filed category counts for more than any single
 * shared tag, and tags accumulate, so a post three tags deep in a different
 * category can still out-rank a same-category post with nothing else in common.
 *
 * Ties break on *publication proximity* rather than plain recency. Plain
 * recency would re-introduce the original bug in a smaller form — with three
 * categories over eighteen posts, every article in a category would surface the
 * same two newest members of it. Preferring the posts published either side of
 * this one gives each article a distinct pair while still reading as "from
 * around the same time".
 *
 * Deterministic on purpose: this runs in the loader and the result is rendered
 * during SSR, so a shuffle would produce a hydration mismatch.
 */

const SAME_CATEGORY_SCORE = 3;
const SHARED_TAG_SCORE = 1;
const RELATED_LIMIT = 2;

export interface RelatedCandidate {
  category: string;
  id: string;
  tags: string[] | null;
}

function normalizeTags(tags: string[] | null | undefined): Set<string> {
  const normalized = new Set<string>();
  for (const tag of tags ?? []) {
    const trimmed = tag.trim().toLowerCase();
    if (trimmed) {
      normalized.add(trimmed);
    }
  }
  return normalized;
}

export function relevanceScore(
  article: RelatedCandidate,
  candidate: RelatedCandidate,
  articleTags: Set<string> = normalizeTags(article.tags)
): number {
  let score = article.category === candidate.category ? SAME_CATEGORY_SCORE : 0;
  for (const tag of normalizeTags(candidate.tags)) {
    if (articleTags.has(tag)) {
      score += SHARED_TAG_SCORE;
    }
  }
  return score;
}

/**
 * `all` is expected in the listing's own order (newest first), which is what
 * the publication-proximity tiebreak reads.
 */
export function pickRelatedArticles<T extends RelatedCandidate>(
  article: RelatedCandidate,
  all: readonly T[],
  limit: number = RELATED_LIMIT
): T[] {
  const articleTags = normalizeTags(article.tags);
  const ownIndex = all.findIndex((other) => other.id === article.id);

  return all
    .map((candidate, index) => ({
      candidate,
      distance: ownIndex === -1 ? index : Math.abs(index - ownIndex),
      index,
      score: relevanceScore(article, candidate, articleTags),
    }))
    .filter((entry) => entry.candidate.id !== article.id)
    .sort(
      (a, b) =>
        b.score - a.score || a.distance - b.distance || a.index - b.index
    )
    .slice(0, limit)
    .map((entry) => entry.candidate);
}
